import { useEffect, useRef, useState } from "react";
import { FileText, ImageUp, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { API, api, errorMessage } from "@/lib/api";
import { NUMBER_PRESETS, RESET_OPTIONS, renderNumber, unknownTokens } from "@/lib/numbering";
import { AccessPanel } from "@/components/AccessPanel";
import { ApiKeysPanel } from "@/components/ApiKeysPanel";
import { LetterheadPreview } from "@/components/LetterheadPreview";
import { useAuth } from "@/context/AuthContext";

const Section = ({ title, hint, children, testId }) => (
  <section className="surface p-6" data-testid={testId}>
    <h2 className="font-serif text-xl text-slate-900">{title}</h2>
    {hint ? <p className="mt-1.5 text-[12px] text-slate-500">{hint}</p> : null}
    <div className="mt-6 grid gap-4 sm:grid-cols-2">{children}</div>
  </section>
);

const Wrapped = ({ label, span, children }) => (
  <div className={`space-y-2 ${span ? "sm:col-span-2" : ""}`}>
    <Label className="label-caps">{label}</Label>
    {children}
  </div>
);

const isPdf = (path) => String(path || "").toLowerCase().endsWith(".pdf");

// Uploading refreshes the saved settings; only the asset paths are pulled back into the
// form so anything typed but not yet saved survives.
const assetPaths = (s) => ({
  logo_path: s.logo_path,
  signature_path: s.signature_path,
  pan_card_path: s.pan_card_path,
  cancelled_cheque_path: s.cancelled_cheque_path,
  docs_token: s.docs_token,
});

const AssetSlot = ({
  kind,
  label,
  hint,
  inputRef,
  path,
  uploading,
  onUpload,
  onRemove,
  accept = "image/png,image/jpeg,image/webp",
}) => (
  <div className="space-y-3" data-testid={`asset-slot-${kind}`}>
    <div>
      <Label className="label-caps">{label}</Label>
      <p className="mt-1 text-[11px] text-slate-500">{hint}</p>
    </div>
    <div className="flex min-h-[104px] items-center justify-center border border-dashed border-slate-300 bg-slate-50 p-4">
      {path ? (
        isPdf(path) ? (
          <a
            href={`${API}/files/${path}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`asset-preview-${kind}`}
            className="inline-flex items-center gap-2 text-[12px] font-semibold text-slate-700 underline decoration-slate-300 transition-colors duration-150 hover:text-slate-900"
          >
            <FileText className="h-4 w-4" strokeWidth={1.6} />
            Open the uploaded PDF
          </a>
        ) : (
          <img
            src={`${API}/files/${path}`}
            alt={label}
            className="max-h-20 max-w-full object-contain"
            data-testid={`asset-preview-${kind}`}
          />
        )
      ) : (
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">Not uploaded</p>
      )}
    </div>
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      className="hidden"
      data-testid={`asset-input-${kind}`}
      onChange={(e) => {
        onUpload(kind, e.target.files?.[0]);
        e.target.value = "";
      }}
    />
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading === kind}
        data-testid={`asset-upload-${kind}`}
        className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50 disabled:opacity-60"
      >
        {uploading === kind ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <ImageUp className="h-3.5 w-3.5" strokeWidth={1.8} />
        )}
        {path ? "Replace" : "Upload"}
      </button>
      {path ? (
        <button
          type="button"
          onClick={() => onRemove(kind)}
          disabled={uploading === kind}
          data-testid={`asset-remove-${kind}`}
          className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-[12px] font-semibold text-slate-600 transition-colors duration-150 hover:border-red-300 hover:text-red-600 disabled:opacity-60"
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
          Remove
        </button>
      ) : null}
    </div>
  </div>
);

export default function SettingsPage() {
  const { settings, setSettings, loadSettings } = useAuth();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const logoInput = useRef(null);
  const signInput = useRef(null);
  const panInput = useRef(null);
  const chequeInput = useRef(null);
  const hydrated = useRef(false);
  const countersTouched = useRef(false);

  useEffect(() => {
    // hydrate once — a refresh after an upload must not wipe fields typed but not yet saved
    if (hydrated.current) return;
    if (settings) {
      setForm(settings);
      hydrated.current = true;
    } else {
      loadSettings().then((data) => {
        if (data) {
          setForm(data);
          hydrated.current = true;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  if (!form) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="settings-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const field = (key, extra = {}) => ({
    value: form[key] ?? "",
    onChange: (e) => {
      if (key === "next_number" || key === "adhoc_next_number") countersTouched.current = true;
      setForm((f) => ({ ...f, [key]: e.target.value }));
    },
    className: `rounded-sm ${extra.mono ? "num" : ""}`.trim(),
    "data-testid": `settings-${key.replace(/_/g, "-")}`,
  });

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      // the counters move every time a fee note is raised — never write a stale one back
      const live = countersTouched.current ? null : await loadSettings();
      const payload = {
        ...form,
        default_tax_rate: Number(form.default_tax_rate || 0),
        payment_terms_days: Number(form.payment_terms_days || 0),
        next_number: Number((live ? live.next_number : form.next_number) || 1),
        adhoc_next_number: Number((live ? live.adhoc_next_number : form.adhoc_next_number) || 1),
        number_padding: Number(form.number_padding || 4),
        cc_emails: (form.cc_emails || [])
          .map((address) => (address || "").trim())
          .filter(Boolean)
          .slice(0, 3),
      };
      const { data } = await api.put("/settings", payload);
      setSettings(data);
      setForm(data);
      countersTouched.current = false;
      toast.success("Chambers settings saved");
    } catch (error) {
      toast.error(errorMessage(error, "Could not save settings"));
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post(`/uploads/brand/${kind}`, body);
      const fresh = await loadSettings();
      if (fresh) {
        setSettings(fresh);
        setForm((f) => ({ ...f, ...assetPaths(fresh) }));
      }
      toast.success(
        {
          logo: "Crest uploaded",
          signature: "Signature uploaded",
          pan_card: "PAN card scan uploaded",
          cancelled_cheque: "Cancelled cheque scan uploaded",
        }[kind] || "Uploaded",
      );
    } catch (error) {
      toast.error(errorMessage(error, "Upload failed"));
    } finally {
      setUploading("");
    }
  };

  const removeAsset = async (kind) => {
    setUploading(kind);
    try {
      await api.delete(`/uploads/brand/${kind}`);
      const fresh = await loadSettings();
      if (fresh) {
        setSettings(fresh);
        setForm((f) => ({ ...f, ...assetPaths(fresh) }));
      }
      toast.success("Removed");
    } catch (error) {
      toast.error(errorMessage(error, "Could not remove image"));
    } finally {
      setUploading("");
    }
  };

  return (
    <form className="space-y-6" onSubmit={save} data-testid="settings-page">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-caps">Practice profile</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            Chambers settings
          </h1>
          <p className="mt-3 max-w-xl text-sm text-slate-600">
            Everything here prints on the invoice letterhead and drives the email and WhatsApp
            templates.
          </p>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="save-settings-button"
          className="inline-flex items-center gap-2 bg-slate-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" strokeWidth={1.8} />
          )}
          Save settings
        </button>
      </header>

      <Section
        title="Letterhead"
        hint="Appears at the head of every generated PDF."
        testId="settings-letterhead"
      >
        <Wrapped label="Counsel name">
          <Input {...field("counsel_name")} placeholder="Aditi R. Menon" />
        </Wrapped>
        <Wrapped label="Designation">
          <Input {...field("designation")} placeholder="Advocate, High Court" />
        </Wrapped>
        <Wrapped label="Chambers name" span>
          <Input {...field("chambers_name")} />
        </Wrapped>
        <Wrapped label="Address" span>
          <Textarea {...field("address")} rows={3} />
        </Wrapped>
        <Wrapped label="Phone">
          <Input {...field("phone", { mono: true })} />
        </Wrapped>
        <Wrapped label="Email">
          <Input {...field("email")} type="email" />
        </Wrapped>
        <Wrapped label="Website">
          <Input {...field("website")} />
        </Wrapped>
        <Wrapped label="Bar enrolment / council ID">
          <Input {...field("bar_council_id", { mono: true })} />
        </Wrapped>
        <Wrapped label="Invoice template">
          <Select
            value={form.template_style || "classic"}
            onValueChange={(v) => setForm((f) => ({ ...f, template_style: v }))}
          >
            <SelectTrigger className="rounded-sm" data-testid="settings-template-style">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="classic">Classic — centred serif letterhead</SelectItem>
              <SelectItem value="modern">Modern — left rule, sans-serif</SelectItem>
              <SelectItem value="minimal">Minimal — hairline, no fills</SelectItem>
            </SelectContent>
          </Select>
        </Wrapped>
        <Wrapped label="Show on the fee note">
          <Select
            value={form.default_party_display || "both"}
            onValueChange={(v) => setForm((f) => ({ ...f, default_party_display: v }))}
          >
            <SelectTrigger className="rounded-sm" data-testid="settings-default-party-display">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="both">Billing party &amp; client (default)</SelectItem>
              <SelectItem value="billing">Billing party only</SelectItem>
              <SelectItem value="client">Client only</SelectItem>
            </SelectContent>
          </Select>
        </Wrapped>
      </Section>

      <Section
        title="Crest &amp; signature"
        hint="PNG, JPG or WEBP under 2 MB. The crest prints beside your letterhead and the signature above your name on every invoice PDF."
        testId="settings-brand"
      >
        <AssetSlot
          kind="logo"
          label="Chambers crest / logo"
          hint="Transparent PNG works best. Printed up to 32mm wide."
          inputRef={logoInput}
          path={form.logo_path}
          uploading={uploading}
          onUpload={uploadAsset}
          onRemove={removeAsset}
        />
        <AssetSlot
          kind="signature"
          label="Signature"
          hint="A scan of your signature on white or transparent background."
          inputRef={signInput}
          path={form.signature_path}
          uploading={uploading}
          onUpload={uploadAsset}
          onRemove={removeAsset}
        />
      </Section>

      <LetterheadPreview form={form} />

      <Section title="Tax &amp; currency" testId="settings-tax">
        <div className="flex items-center justify-between gap-4 border border-slate-200 px-4 py-3 sm:col-span-2">
          <div>
            <p className="text-[13px] font-semibold text-slate-900">
              Add {form.tax_label || "GST"} to fee notes
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              Switch this off if you do not charge {form.tax_label || "GST"} — new fee notes are then
              raised at nil tax and the tax row is dropped from the PDF altogether.
            </p>
          </div>
          <Switch
            checked={form.tax_enabled !== false}
            onCheckedChange={(v) => setForm((f) => ({ ...f, tax_enabled: v }))}
            data-testid="settings-tax-enabled-switch"
          />
        </div>
        <Wrapped label="Tax label">
          <Input {...field("tax_label")} placeholder="GST / VAT" />
        </Wrapped>
        <Wrapped label="Tax / GST identifier">
          <Input {...field("tax_id", { mono: true })} />
        </Wrapped>
        <Wrapped label="Default tax rate %">
          <Input
            {...field("default_tax_rate", { mono: true })}
            type="number"
            step="0.5"
            min="0"
            disabled={form.tax_enabled === false}
          />
        </Wrapped>
        <Wrapped label="Currency code">
          <Input {...field("currency_code", { mono: true })} />
        </Wrapped>
        <Wrapped label="Currency prefix on invoices">
          <Input {...field("currency_symbol", { mono: true })} placeholder="Rs." />
        </Wrapped>
        <Wrapped label="Payment terms (days)">
          <Input {...field("payment_terms_days", { mono: true })} type="number" min="0" />
        </Wrapped>
      </Section>

      <Section
        title="Remittance details"
        hint="Printed in the remittance block of the PDF."
        testId="settings-bank"
      >
        <Wrapped label="Account name">
          <Input {...field("bank_account_name")} />
        </Wrapped>
        <Wrapped label="Bank &amp; branch">
          <Input {...field("bank_name")} />
        </Wrapped>
        <Wrapped label="Account number">
          <Input {...field("bank_account_number", { mono: true })} />
        </Wrapped>
        <Wrapped label="IFSC">
          <Input {...field("bank_ifsc", { mono: true })} />
        </Wrapped>
        <Wrapped label="SWIFT">
          <Input {...field("bank_swift", { mono: true })} />
        </Wrapped>
        <Wrapped label="UPI ID">
          <Input {...field("upi_id", { mono: true })} />
        </Wrapped>
        <Wrapped label="PAN">
          <Input {...field("pan_number", { mono: true })} placeholder="ABCDE1234F" />
        </Wrapped>
      </Section>

      <Section
        title="PAN card &amp; cancelled cheque"
        hint="PDF, PNG, JPG or WEBP under 5 MB. Whatever is uploaded here is linked at the bottom of every fee-note email as 'Also enclosed', so the instructing lawyer can download the scan without asking for it."
        testId="settings-practice-docs"
      >
        <AssetSlot
          kind="pan_card"
          label="PAN card scan"
          hint="The scanned copy sent with fee notes."
          inputRef={panInput}
          path={form.pan_card_path}
          uploading={uploading}
          onUpload={uploadAsset}
          onRemove={removeAsset}
          accept="application/pdf,image/png,image/jpeg,image/webp"
        />
        <AssetSlot
          kind="cancelled_cheque"
          label="Cancelled cheque scan"
          hint="For the client's remittance paperwork."
          inputRef={chequeInput}
          path={form.cancelled_cheque_path}
          uploading={uploading}
          onUpload={uploadAsset}
          onRemove={removeAsset}
          accept="application/pdf,image/png,image/jpeg,image/webp"
        />
      </Section>

      <Section
        title="Invoice numbering"
        hint="Tokens you can use: {prefix} {seq} {yyyy} {yy} {mm} {mon} {fy}. {seq} is the running number and is required."
        testId="settings-numbering"
      >
        <Wrapped label="Prefix">
          <Input {...field("invoice_prefix", { mono: true })} placeholder="INV-" />
        </Wrapped>
        <Wrapped label="Next number">
          <Input {...field("next_number", { mono: true })} type="number" min="1" max="999999" />
        </Wrapped>
        <Wrapped label="One-off (ad hoc) prefix">
          <Input {...field("adhoc_prefix", { mono: true })} placeholder="ADH-" />
        </Wrapped>
        <Wrapped label="Next one-off number">
          <Input {...field("adhoc_next_number", { mono: true })} type="number" min="1" max="999999" />
        </Wrapped>
        <Wrapped label="Zero padding">
          <Input {...field("number_padding", { mono: true })} type="number" min="1" max="8" />
        </Wrapped>
        <Wrapped label="Start numbering again">
          <Select
            value={form.number_reset || "never"}
            onValueChange={(v) => setForm((f) => ({ ...f, number_reset: v }))}
          >
            <SelectTrigger className="rounded-sm" data-testid="settings-number-reset">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RESET_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Wrapped>
        <Wrapped label="Reference format" span>
          <Input {...field("number_format", { mono: true })} placeholder="{prefix}{yyyy}-{seq}" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {NUMBER_PRESETS.map((p) => (
              <button
                key={p.pattern}
                type="button"
                onClick={() => setForm((f) => ({ ...f, number_format: p.pattern }))}
                data-testid={`number-preset-${p.pattern.replace(/[{}/]/g, "")}`}
                className={`num border px-2.5 py-1 text-[11px] transition-colors duration-150 ${
                  (form.number_format || "{prefix}{seq}") === p.pattern
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="mt-3 text-[12px] text-slate-600" data-testid="numbering-preview">
            Next fee note:{" "}
            <span className="num font-semibold text-slate-900">
              {renderNumber({
                pattern: form.number_format,
                prefix: form.invoice_prefix,
                padding: form.number_padding,
                sequence: form.next_number,
              })}
            </span>
            {"  ·  one-off: "}
            <span className="num font-semibold text-slate-900">
              {renderNumber({
                pattern: form.number_format,
                prefix: form.adhoc_prefix,
                padding: form.number_padding,
                sequence: form.adhoc_next_number,
              })}
            </span>
          </p>
          <p className="mt-1.5 text-[11px] text-slate-500">
            {form.number_reset === "never" || !form.number_reset
              ? "One running series — the number never restarts."
              : "Each new period starts again at 1. A fee note takes the period of its own invoice date, and no number is ever reused."}
          </p>
          {unknownTokens(form.number_format).length > 0 ? (
            <p className="mt-1.5 text-[11px] text-amber-700" data-testid="numbering-unknown-token">
              {unknownTokens(form.number_format).join(", ")} is not a token I know — it will print
              exactly as typed.
            </p>
          ) : null}
        </Wrapped>
        <Wrapped label="Footer note on invoice" span>
          <Textarea {...field("invoice_footer_note")} rows={2} />
        </Wrapped>
      </Section>

      <Section
        title="Delivery templates"
        hint="Placeholders: {contact_name} {invoice_number} {case_title} {matter_ref} {amount} {due_date} {counsel_name} {link}"
        testId="settings-templates"
      >
        <Wrapped label="Email subject" span>
          <Input {...field("email_subject_template")} />
        </Wrapped>
        <Wrapped label="Email body" span>
          <Textarea {...field("email_body_template")} rows={9} />
        </Wrapped>
        <Wrapped label="WhatsApp message" span>
          <Textarea {...field("whatsapp_template")} rows={8} />
        </Wrapped>
      </Section>

      <Section
        title="Payment reminder"
        hint="Sent when you press 'Send reminder' on an unpaid invoice. Extra placeholders: {balance_due} {days_overdue} {amount_paid}"
        testId="settings-reminder"
      >
        <Wrapped label="Reminder subject" span>
          <Input {...field("reminder_subject_template")} />
        </Wrapped>
        <Wrapped label="Reminder body" span>
          <Textarea {...field("reminder_body_template")} rows={10} />
        </Wrapped>
      </Section>

      <Section
        title="Payment receipt"
        hint="Used for the receipt PDF and the receipt email/WhatsApp. Placeholders: {contact_name} {receipt_no} {invoice_number} {case_title} {matter_ref} {amount} {paid_on} {balance_due} {counsel_name} {link}"
        testId="settings-receipt"
      >
        <Wrapped label="Receipt email subject" span>
          <Input {...field("receipt_subject_template")} />
        </Wrapped>
        <Wrapped label="Receipt email body" span>
          <Textarea {...field("receipt_body_template")} rows={9} />
        </Wrapped>
        <Wrapped label="Receipt WhatsApp message" span>
          <Textarea {...field("receipt_whatsapp_template")} rows={8} />
        </Wrapped>
        <Wrapped label="Note printed on the receipt" span>
          <Textarea {...field("receipt_footer_note")} rows={2} />
        </Wrapped>
      </Section>

      <Section
        title="Statement of account"
        hint="Placeholders: {contact_name} {account_name} {as_at} {period} {billed} {received} {outstanding} {overdue} {invoice_count} {counsel_name} {link}"
        testId="settings-statement"
      >
        <Wrapped label="Statement email subject" span>
          <Input {...field("statement_subject_template")} />
        </Wrapped>
        <Wrapped label="Statement email body" span>
          <Textarea {...field("statement_body_template")} rows={9} />
        </Wrapped>
        <Wrapped label="Statement WhatsApp message" span>
          <Textarea {...field("statement_whatsapp_template")} rows={8} />
        </Wrapped>
      </Section>

      <Section
        title="Revised fee note"
        hint="Used instead of the usual covering note when you resend a fee note you have edited. Placeholders: {contact_name} {invoice_number} {revision} {case_title} {matter_ref} {amount} {amount_paid} {balance_due} {due_date} {issue_date} {counsel_name} {link}"
        testId="settings-revision"
      >
        <Wrapped label="Revised email subject" span>
          <Input {...field("revision_subject_template")} />
        </Wrapped>
        <Wrapped label="Revised email body" span>
          <Textarea {...field("revision_body_template")} rows={11} />
        </Wrapped>
      </Section>

      <Section
        title="Copies of every email"
        hint="Where replies land, and up to three addresses of your own — a clerk, a junior, your own second inbox — copied on every fee note, receipt, reminder, statement and account-page email the app sends."
        testId="settings-cc"
      >
        <Wrapped label="Replies come to" span>
          <Input
            type="email"
            value={form.email_reply_to || ""}
            onChange={(e) => setForm((f) => ({ ...f, email_reply_to: e.target.value }))}
            placeholder="Leave blank to use your chambers email above"
            className="rounded-sm"
            data-testid="settings-reply-to"
          />
        </Wrapped>
        {[0, 1, 2].map((index) => (
          <Wrapped key={index} label={`Copy ${index + 1}`}>
            <Input
              type="email"
              value={(form.cc_emails || [])[index] || ""}
              onChange={(e) =>
                setForm((f) => {
                  const next = [...(f.cc_emails || [])];
                  while (next.length < 3) next.push("");
                  next[index] = e.target.value;
                  return { ...f, cc_emails: next };
                })
              }
              placeholder="clerk@chambers.in"
              className="rounded-sm"
              data-testid={`settings-cc-email-${index}`}
            />
          </Wrapped>
        ))}
        <p className="text-[11px] leading-relaxed text-slate-500 sm:col-span-2">
          Copies appear as additional recipients on the message, so the instructing lawyer can see
          who else has the fee note. Leave a box blank to drop that copy. Emails go out under the
          name <span className="text-slate-800">Chambers of Amir Arsiwala</span>, and the foot of
          every email carries this reply address in writing so the lawyer reading it sees where to
          write to you.
        </p>
      </Section>

      <Section
        title="Client account pages"
        hint="The private live-balance link you share with an instructing lawyer or a whole firm. Placeholders: {contact_name} {account_name} {as_at} {billed} {received} {outstanding} {overdue} {invoice_count} {counsel_name} {link}"
        testId="settings-portal"
      >
        <div className="flex items-center justify-between gap-4 border border-slate-200 px-4 py-3 sm:col-span-2">
          <div>
            <p className="text-[13px] font-semibold text-slate-900">
              Email me when an account page is opened
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              One alert per link every six hours, so a lawyer refreshing the page never floods your
              inbox.
            </p>
          </div>
          <Switch
            checked={form.portal_alert_email !== false}
            onCheckedChange={(v) => setForm((f) => ({ ...f, portal_alert_email: v }))}
            data-testid="settings-portal-alert-switch"
          />
        </div>
        <Wrapped label="Send alerts to (blank = chambers email)" span>
          <Input {...field("portal_alert_recipient")} type="email" />
        </Wrapped>
        <Wrapped label="Share email subject" span>
          <Input {...field("portal_subject_template")} />
        </Wrapped>
        <Wrapped label="Share email body" span>
          <Textarea {...field("portal_body_template")} rows={9} />
        </Wrapped>
        <Wrapped label="Share WhatsApp message" span>
          <Textarea {...field("portal_whatsapp_template")} rows={7} />
        </Wrapped>
      </Section>

      <AccessPanel />

      <ApiKeysPanel />

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          data-testid="save-settings-button-bottom"
          className="inline-flex items-center gap-2 bg-slate-900 px-6 py-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" strokeWidth={1.8} />
          )}
          Save settings
        </button>
      </div>
    </form>
  );
}
